import React from 'react';
import { dualCpuFlashEligible, dualCpuFlashIneligibleReason } from '../../domain/rules/dualCpuRules';
import { FLASH_NODE_MODELS } from '../../utils/partNumbers.jsx';
import { DUAL_CPU_FLASH_FEATURE_ENABLED } from '../../utils/dualCpuFlashFeature';

/**
 * CONF-210. The classic cards' Flash Node Model control: Single CPU
 * (VCH-5000-F1N) or Dual CPU (VCH-5500-F1N, 2x PCIe + 1x OCP NICs, higher
 * per-node throughput). Shared by BaselineConfigCard and ExpansionConfigCard.
 *
 * dualCpuAccess-only (CONF-259; was execAccess). Hidden from everyone else
 * unless the quote already carries the Dual CPU node, in which case it renders
 * locked so anyone else loading the quote can see what they are quoting (user
 * decision 2026-09-25). Locked with
 * the vendor's reason when the vendor cannot supply the node; R13 will
 * already have reverted the value in that case. With the kill switch off the
 * feature is pulled: nobody is offered a locked control for it, and only a
 * quote that already carries the node still shows it (locked, with the
 * release reason).
 *
 * Writes afaNodeModel explicitly, never a default: a fresh config carries no
 * key so the frozen snapshots stay put (see domain/flatConfig.ts).
 */
export const FlashNodeModelToggle = ({ config, updateConfig, hasDualCpuAccess }) => {
  const isDual = config.afaNodeModel === 'dualCpu';
  if (!isDual && !(hasDualCpuAccess && DUAL_CPU_FLASH_FEATURE_ENABLED)) return null;

  const vendorReason = dualCpuFlashIneligibleReason(config.hardwareVendor);
  const locked = !hasDualCpuAccess || !dualCpuFlashEligible(config.hardwareVendor);
  const title = !hasDualCpuAccess
    ? 'Requires Dual CPU access'
    : vendorReason || `${FLASH_NODE_MODELS.dualCpu.partNumber}: 2x PCIe + 1x OCP NICs, higher per-node throughput`;

  return (
    <div className="grid grid-cols-10 gap-2">
      <div className="col-span-6">
        <label>Flash Node Model</label>
        <div className="flex items-center gap-3 mt-2">
          <span className="text-sm">Single CPU</span>
          <button
            type="button"
            aria-label="Flash Node Model"
            aria-pressed={isDual}
            disabled={locked}
            aria-disabled={locked}
            title={title}
            onClick={() => updateConfig({ afaNodeModel: isDual ? 'standard' : 'dualCpu' })}
            className={`relative inline-flex h-6 w-16 items-center rounded-full ${
              isDual ? 'bg-orange-500' : 'bg-gray-700'
            } transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <span
              className={`${
                isDual ? 'translate-x-11' : 'translate-x-1'
              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
            />
          </button>
          <span className="text-sm">Dual CPU</span>
        </div>
        <span className="text-xs text-gray-400">{title}</span>
      </div>
    </div>
  );
};
